import { Chat as ChatIcon } from "@mui/icons-material";
import type { ColumnDef } from "@tanstack/react-table";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import AIChat, { type AIChatHandle } from "~/components/aiChat";
import ChatHistoryPanel from "~/components/aiChat/components/ChatHistoryPanel";
import DiffViewer, { type DiffViewerHandle } from "~/components/aiChat/diff";
import CommonDocumentHeader from "~/components/Document/commonDocumentHeader";
import ExportModal, { type TableExportData } from "~/components/Document/exportModal";
import { MarkdownContent, markdownContentClassName } from "~/components/Document/MarkdownContent";
import MarkdownEditorPreview from "~/components/Document/MarkdownEditorPreview";
import KnowledgeIndicator from "~/components/Project/knowledge/KnowledgeIndicator";
import KnowledgeSidebar from "~/components/Project/knowledge/KnowledgeSidebar";
import { StructuredDiffErrorBoundary } from "~/components/StructuredDiffViewer/StructuredDiffErrorBoundary";
import {
  type StructuredDiffPayload,
  StructuredDiffViewer,
} from "~/components/StructuredDiffViewer/StructuredDiffViewer";
import Button from "~/components/ui/button";
import Modal from "~/components/ui/modal";
import TextareaAutosize from "~/components/ui/textarea";
import { MAX_APPS } from "~/constants/appGenerationLimits";
import { TOAST_MESSAGES } from "~/constants/messages";
import AppDesignTabs from "~/features/appDesign/appDesignTabs";
import { AIChatApi } from "~/functions/api/aiChat";
import { useNavigationPrompt } from "~/functions/navigationHooks";
import { useKnowledgeCount } from "~/hooks/useKnowledgeCount";
import { useMarkdownRenderer } from "~/hooks/useMarkdownRenderer";
import { useProjectMembers } from "~/hooks/useProjectMembers";
import { useProjectPermission } from "~/hooks/useProjectPermission";
import { useStructuredDocumentApply } from "~/hooks/useStructuredDocumentApply";
import DeleteIcon from "~/images/delete_row.svg?react";
import {
  acquireAppDesignLock,
  addAppRow,
  addConnectorRow,
  addPoint,
  addRequirementRow,
  deleteAppRow,
  deleteConnectorRow,
  deletePoint,
  deleteRequirementRow,
  fetchAppDesign,
  releaseAppDesignLock,
  restoreFromCopyData,
  updateAppDesign,
  updateAppRow,
  updateConnectorRow,
  updateMarkdownContent,
  updatePoint,
  updateRequirementRow,
} from "~/slices/appDesignSlice";
import { initializeSessions } from "~/slices/projectSlice";
import type { AppDispatch, RootState } from "~/store";
import chatStyles from "../systemProposal/styles.module.scss";
import styles from "./index.module.scss";

// interface定義
interface AppData {
  appid: number;
  appname: string;
  description: string;
}

interface ConnectorData {
  connectorid: number;
  sid: number;
  tid: number;
  relationship: string;
  keyfield: string;
  referencefield: string;
}

interface RequirementData {
  appid: number;
  rid: string;
  name: string;
  description: string;
  how: string;
}

const AppDesign = () => {
  const { renderMarkdown } = useMarkdownRenderer();
  const dispatch = useDispatch<AppDispatch>();
  const { selectedId } = useParams<{ selectedId: string }>();
  const { error: projectError } = useProjectMembers();

  const {
    appData: storeAppData,
    connectorData: storeConnectorData,
    requirementData: storeRequirementData,
    points: storePoints,
    isEditable: isEditableFromStore,
    error,
    isLoading,
    documentInfo,
    isMarkdown,
    markdownContent,
    rawContent,
  } = useSelector((state: RootState) => state.appDesign);
  const { currentUser, currentProject } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const { canAccessChat } = useProjectPermission();
  // const location = useLocation();

  // チャットアイコンの表示可否を判定
  const shouldShowChatIcon = canAccessChat();
  const [currentTabIndex, setCurrentTabIndex] = useState(0);
  const [showExportModal, setShowExportModal] = useState(false);
  // ナレッジインジケーター用のカウント取得
  const { count: knowledgeCount, totalCount: knowledgeTotalCount } =
    useKnowledgeCount("app-design");

  // ナレッジサイドバーの状態
  const [isKnowledgeSidebarOpen, setIsKnowledgeSidebarOpen] = useState(false);

  // 編集モードの状態管理はReduxのステートを使用
  const isEditable = isEditableFromStore;

  // AIChat関連の状態
  const { currentSession } = useSelector((state: RootState) => state.project);
  const [showAIChat, setShowAIChat] = useState(true);
  const [isDiffModalOpen, setDiffModalOpen] = useState(false);
  const [showChatHistory, setShowChatHistory] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [threads, setThreads] = useState<
    { id?: string; threadId?: string; title?: string; createdAt?: string; updatedAt?: string }[]
  >([]);
  const aiChatRef = React.useRef<AIChatHandle>(null);
  const diffViewerRef = React.useRef<DiffViewerHandle>(null);
  const [currentThreadId, setCurrentThreadId] = useState<string>("");
  const [documentBefore, setDocumentBefore] = useState<unknown>(null);
  const [documentAfter, setDocumentAfter] = useState<unknown>(null);
  const [structuredDiff, setStructuredDiff] = useState<StructuredDiffPayload | null>(null);
  const [isDiffSaving, setIsDiffSaving] = useState(false);
  const [isDiffValid, setIsDiffValid] = useState(true);
  const [diffViewerHeader, setDiffViewerHeader] = useState<React.ReactNode>(null);
  const shouldUseMarkdownMode = isMarkdown;
  const isStructuredDocument = !isMarkdown;
  const handleStructuredDocumentApply = useStructuredDocumentApply({
    acquireLock: () => dispatch(acquireAppDesignLock()).unwrap(),
    releaseLock: () => dispatch(releaseAppDesignLock()),
    fetchDocument: () => dispatch(fetchAppDesign()),
    organizationId: currentProject?.id ? currentUser.organization_id : "",
    projectId: currentProject?.id || "",
    documentId: currentProject?.outputs?.["app-design"]?.id || "",
    setIsDiffSaving,
    logPrefix: "AppDesign",
  });
  const handleExportClick = () => {
    setShowExportModal(true);
  };

  const getExportData = (): TableExportData => {
    const name = "アプリ設計書";

    if (shouldUseMarkdownMode) {
      return {
        headers: [],
        rows: [],
        name,
        markdownContent: markdownContent,
      };
    }

    const headers: string[] = [];
    const rows: string[][] = [];

    // アプリテーブルのヘッダーとデータを追加
    headers.push("作成するアプリ: アプリID", "アプリ名", "説明");
    storeAppData.forEach((row) => {
      rows.push([row.appid.toString(), row.appname, row.description]);
    });

    // 空行を追加して次のテーブルと区切る
    rows.push([]);

    // アプリ間連携テーブルのヘッダーとデータを追加
    headers.push(
      "アプリ間の連携: 連携ID",
      "データ元アプリID",
      "連携先アプリID",
      "連携方法",
      "キーフィールド",
      "参照フィールド",
    );
    storeConnectorData.forEach((row) => {
      rows.push([
        row.connectorid.toString(),
        row.sid.toString(),
        row.tid.toString(),
        row.relationship,
        row.keyfield,
        row.referencefield,
      ]);
    });

    // 空行を追加して次のテーブルと区切る
    rows.push([]);

    // 要求機能テーブルのヘッダーとデータを追加
    headers.push("アプリ毎の機能: アプリID", "機能ID", "機能", "説明", "実装方法");
    storeRequirementData.forEach((row) => {
      rows.push([row.appid.toString(), row.rid.toString(), row.name, row.description, row.how]);
    });
    // 空行を追加して次のテーブルと区切る
    rows.push([]);

    headers.push("考慮点");
    storePoints.forEach((row) => {
      rows.push([row]);
    });

    return { headers, rows, name };
  };

  const handleLock = async () => {
    await dispatch(acquireAppDesignLock());
  };

  const handleCancel = async () => {
    dispatch(restoreFromCopyData());
    await dispatch(releaseAppDesignLock());
  };

  const handleUpdate = async () => {
    await dispatch(updateAppDesign());
  };

  useNavigationPrompt(
    isEditable,
    "編集内容が保存されていません。ページを離れてもよろしいですか？",
    handleUpdate,
    handleCancel,
  );

  const updateData = (
    rowIndex: number,
    columnId: string,
    value: string | number,
    tabIndex: number,
  ) => {
    if (shouldUseMarkdownMode) return;
    if (tabIndex === 0) {
      const updated = { ...storeAppData[rowIndex]!, [columnId]: value } as AppData;
      dispatch(updateAppRow({ index: rowIndex, data: updated }));
    } else if (tabIndex === 1) {
      const updated = { ...storeConnectorData[rowIndex]!, [columnId]: value } as ConnectorData;
      dispatch(updateConnectorRow({ index: rowIndex, data: updated }));
    } else if (tabIndex === 2) {
      const updated = { ...storeRequirementData[rowIndex]!, [columnId]: value } as RequirementData;
      dispatch(updateRequirementRow({ index: rowIndex, data: updated }));
    }
  };

  // アプリIDからアプリ名を取得するヘルパー関数
  const getAppNameById = (appId: number): string => {
    const app = storeAppData.find((a) => a.appid === appId);
    return app?.appname ?? String(appId);
  };

  const indexedPoints = useMemo(
    () => storePoints.map((text, idx) => ({ text, idx, id: `pt-${idx}` })),
    [storePoints],
  );

  const columns1: ColumnDef<object>[] = [
    { accessorKey: "appname", header: "アプリ名" },
    { accessorKey: "description", header: "説明" },
    {
      accessorKey: "delete",
      header: "",
      cell: ({ row }) =>
        isEditable ? (
          <button
            type="button"
            onClick={() => dispatch(deleteAppRow(row.index))}
            style={{ cursor: "pointer", justifyContent: "flex-end" }}
          >
            <DeleteIcon width={16} height={16} />
          </button>
        ) : null,
    },
  ];

  const columns2: ColumnDef<object>[] = [
    {
      accessorKey: "sid",
      header: "データ元アプリ",
      cell: ({ row }) => getAppNameById((row.original as ConnectorData).sid),
    },
    {
      accessorKey: "tid",
      header: "連携先アプリ",
      cell: ({ row }) => getAppNameById((row.original as ConnectorData).tid),
    },
    { accessorKey: "relationship", header: "連携方法" },
    { accessorKey: "keyfield", header: "キーフィールド" },
    { accessorKey: "referencefield", header: "参照フィールド" },
    {
      accessorKey: "delete",
      header: "",
      cell: ({ row }) =>
        isEditable ? (
          <button
            type="button"
            onClick={() => dispatch(deleteConnectorRow(row.index))}
            style={{ cursor: "pointer", justifyContent: "flex-end" }}
          >
            <DeleteIcon width={16} height={16} />
          </button>
        ) : null,
    },
  ];

  const columns3: ColumnDef<object>[] = [
    {
      accessorKey: "appid",
      header: "アプリ名",
      cell: ({ row }) => getAppNameById((row.original as RequirementData).appid),
    },
    { accessorKey: "name", header: "機能" },
    { accessorKey: "description", header: "説明" },
    { accessorKey: "how", header: "実装方法" },
    {
      accessorKey: "delete",
      header: "",
      cell: ({ row }) =>
        isEditable ? (
          <button
            type="button"
            onClick={() => dispatch(deleteRequirementRow(row.index))}
            style={{ cursor: "pointer", justifyContent: "flex-end" }}
          >
            <DeleteIcon width={16} height={16} />
          </button>
        ) : null,
    },
  ];

  const isAppLimitReached = storeAppData.length >= MAX_APPS;

  const addRow = (tabIndex: number) => {
    if (shouldUseMarkdownMode) return;
    if (tabIndex === 0) {
      if (isAppLimitReached) {
        toast.error(`アプリ数の上限（${MAX_APPS}件）に達しました`);
        return;
      }
      // アプリテーブル: 自動連番のIDを生成
      const nextAppId =
        storeAppData.length > 0 ? Math.max(...storeAppData.map((app) => app.appid)) + 1 : 1;
      dispatch(addAppRow({ appid: nextAppId, appname: "", description: "" }));
    } else if (tabIndex === 1) {
      // アプリ間連携テーブル: 自動連番のIDを生成
      const nextConnectorId =
        storeConnectorData.length > 0
          ? Math.max(...storeConnectorData.map((connector) => connector.connectorid)) + 1
          : 1;
      dispatch(
        addConnectorRow({
          connectorid: nextConnectorId,
          sid: 0,
          tid: 0,
          relationship: "",
          keyfield: "",
          referencefield: "",
        }),
      );
    } else if (tabIndex === 2) {
      const hasEmptyRequirement = storeRequirementData.some(
        (req) =>
          req.appid === 0 ||
          String(req.appid).trim() === "" ||
          req.rid.trim() === "" ||
          req.name.trim() === "" ||
          req.description.trim() === "" ||
          req.how.trim() === "",
      );

      if (hasEmptyRequirement) {
        toast.error(
          "空の項目がある行が存在します。すべての項目（アプリ名、機能ID、機能、説明、実装方法）を入力してから新しい行を追加してください。",
        );
        return;
      }

      // 自動連番のIDを生成
      const ridNumbers = storeRequirementData
        .map((req) => parseInt(req.rid, 10))
        .filter((n) => !Number.isNaN(n));
      const nextRidNumber = ridNumbers.length > 0 ? Math.max(...ridNumbers) + 1 : 1;
      const nextRid = String(nextRidNumber);
      dispatch(
        addRequirementRow({
          appid: 0,
          rid: nextRid,
          name: "",
          description: "",
          how: "",
        }),
      );
    }
  };

  useEffect(() => {
    if (!currentProject) {
      navigate(`/${currentUser.organization_id}/home`);
      return;
    }

    // URLパラメータから取得、なければReduxストアから取得
    const documentId = selectedId || currentProject?.outputs?.["app-design"]?.id;

    if (!documentId) {
      toast.error(TOAST_MESSAGES.DOCUMENT.APP_DESIGN_NOT_GENERATED);
      navigate(`/${currentUser.organization_id}/project/${currentProject.id}/dashboard`);
      return;
    }

    dispatch(fetchAppDesign(documentId));
  }, [dispatch, currentProject, currentUser, navigate, selectedId]);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  useEffect(() => {
    if (projectError) {
      console.error("Project data loading error:", projectError);
      toast.error(TOAST_MESSAGES.DOCUMENT.PROJECT_LOAD_FAILED);
    }
  }, [projectError]);

  // Initialize session IDs (reinit on document change)
  const appDesignDocId = currentProject?.outputs?.["app-design"]?.id;
  useEffect(() => {
    if (currentProject?.id && currentUser?.id) {
      dispatch(initializeSessions(appDesignDocId || ""));
    }
  }, [currentProject?.id, currentUser?.id, appDesignDocId, dispatch]);

  // currentSession.threadId が変更されたら currentThreadId を更新
  useEffect(() => {
    if (currentSession?.threadId) {
      setCurrentThreadId(currentSession.threadId);
    }
  }, [currentSession?.threadId]);

  // 履歴を更新する共通関数
  const refreshThreadHistory = async () => {
    // スレッド作成時や手動リロード時に履歴を更新
    setHistoryLoading(true);
    try {
      const api = new AIChatApi();
      const agentId = "commonAgent";
      const rid = currentSession?.resourceId;
      if (!rid) return;
      const list = await api.getThreads(agentId, rid, 10);
      setThreads(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error("[AppDesign] Failed to refresh thread history:", e);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleApprovedDocumentChange = async (approvedDocument: string) => {
    setIsDiffSaving(true);
    try {
      await dispatch(acquireAppDesignLock());
      dispatch(updateMarkdownContent(approvedDocument));
      await dispatch(updateAppDesign());
      await dispatch(fetchAppDesign());
    } catch (error) {
      console.error("Update failed:", error);
      await dispatch(releaseAppDesignLock()).catch(() => {});
    } finally {
      setIsDiffSaving(false);
    }
  };

  const handleDiffClose = () => {
    setIsDiffSaving(false);
    setDiffModalOpen(false);
    setShowAIChat(false);
    setDocumentBefore(null);
    setDocumentAfter(null);
    setStructuredDiff(null);
    setDiffViewerHeader(null);
    setIsDiffValid(true);
  };

  const handleDiffApply = async () => {
    if (isDiffSaving) return;
    if (isStructuredDocument && documentAfter) {
      // For structured documents, save content directly via DocumentApi
      // bypassing the thunk which would serialize from Redux state arrays
      const content =
        typeof documentAfter === "string" ? documentAfter : JSON.stringify(documentAfter);
      const success = await handleStructuredDocumentApply(content);
      if (success) {
        handleDiffClose();
      } else {
        toast.error("ドキュメントの保存に失敗しました");
      }
    } else {
      void diffViewerRef.current?.applyChanges();
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isEditable) {
        // カスタムメッセージ（古いブラウザでは表示される）
        event.preventDefault();
        event.returnValue = ""; // Chrome用: 空文字を指定
      }
    };

    // beforeunload イベントの登録
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      // クリーンアップ
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isEditable]);

  if (isLoading) {
    return <div>ロード中...</div>;
  }
  const toggleEditMode = () => {
    if (isEditable) {
      handleUpdate(); // 保存処理を実行
    } else {
      handleLock();
    }
  };
  const handleHistoryClick = () => {
    const pathSegments = window.location.pathname.split("/");
    pathSegments.pop();
    navigate(`${pathSegments.join("/")}/history`);
  };

  return (
    <div className={"styles['appDesign']"}>
      {documentInfo && (
        <>
          <CommonDocumentHeader
            title="アプリ設計書"
            documentType="app-design"
            createdAt={documentInfo.createdAt}
            {...(documentInfo.modifiedAt && { modifiedAt: documentInfo.modifiedAt })}
            {...(documentInfo.modifiedBy && { modifiedBy: documentInfo.modifiedBy })}
            isEditable={isEditable}
            isLoading={isLoading}
            handleCancel={handleCancel}
            toggleEditMode={toggleEditMode}
            handleExportClick={handleExportClick}
            handleHistoryClick={handleHistoryClick}
            showEditButton={true}
            showExportButton={true}
          />
          {false && (
            <div style={{ padding: "16px 24px", borderBottom: "1px solid #e5e7eb" }}>
              <KnowledgeIndicator
                documentType="app-design"
                activeCount={knowledgeCount}
                totalCount={knowledgeTotalCount}
              />
            </div>
          )}
        </>
      )}

      {showExportModal && (
        <ExportModal
          documentType="app-design"
          onClose={() => setShowExportModal(false)}
          exportData={{
            type: "table",
            exportData: getExportData(),
            name: "app-design",
          }}
        />
      )}

      <div
        style={{
          display: "flex",
          gap: "24px",
          alignItems: "stretch",
          position: "relative",
          minHeight: "calc(100vh - 196px)",
        }}
      >
        <div style={{ flex: "1 1 auto" }}>
          <div className={styles["appDesign-inner"]}>
            {shouldUseMarkdownMode ? (
              <div className={styles["document-content"]}>
                <div className={styles["markdown-mode-content"]}>
                  {isEditable ? (
                    <MarkdownEditorPreview
                      value={markdownContent}
                      onChange={(value) => dispatch(updateMarkdownContent(value))}
                      previewContent={renderMarkdown(markdownContent || "")}
                      textareaClassName={styles["markdown-editor"] ?? ""}
                      previewClassName={markdownContentClassName}
                    />
                  ) : (
                    <MarkdownContent>{renderMarkdown(markdownContent || "")}</MarkdownContent>
                  )}
                </div>
              </div>
            ) : (
              <>
                <AppDesignTabs
                  tabTitles={["作成するアプリ", "アプリ間の連携", "アプリ毎の機能"]}
                  dataSets={[storeAppData, storeConnectorData, storeRequirementData]}
                  columns={[columns1, columns2, columns3]}
                  updateData={updateData}
                  isEditable={isEditable}
                  onTabChange={setCurrentTabIndex}
                  appOptions={storeAppData}
                />
                {isEditable && (
                  <div style={{ marginTop: "10px" }}>
                    <Button
                      label="追加"
                      onClick={() => addRow(currentTabIndex)}
                      styleType="add-row"
                      icon="addRowIcon"
                      disabled={currentTabIndex === 0 && isAppLimitReached}
                    />
                  </div>
                )}
              </>
            )}
          </div>
          {!shouldUseMarkdownMode && currentTabIndex === 2 && !isDiffModalOpen && (
            <>
              <h2 className={styles["points"]}>考慮点</h2>
              {isEditable ? (
                <>
                  {indexedPoints.map(({ text, idx, id }) => (
                    <div
                      key={id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "10px",
                        width: "100%",
                      }}
                    >
                      <div
                        style={{
                          flex: 1,
                          padding: "8px",
                          border: "1px solid #ccc",
                          borderRadius: "5px",
                        }}
                      >
                        <TextareaAutosize
                          value={text}
                          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                            dispatch(updatePoint({ index: idx, value: e.target.value }))
                          }
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => dispatch(deletePoint(idx))}
                        style={{
                          cursor: "pointer",
                          marginLeft: "10px",
                        }}
                      >
                        <DeleteIcon width={16} height={16} />
                      </button>
                    </div>
                  ))}
                  <Button
                    label="考慮点を追加"
                    onClick={() => dispatch(addPoint())}
                    styleType="blue"
                  />
                </>
              ) : (
                <ul>
                  {indexedPoints.map(({ text, id }) => (
                    <li
                      key={id}
                      style={{
                        padding: "8px",
                      }}
                    >
                      {text}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
        {!showAIChat && (
          <KnowledgeSidebar
            isOpen={isKnowledgeSidebarOpen}
            onClose={() => setIsKnowledgeSidebarOpen(false)}
            documentType="app-design"
          />
        )}
        {showAIChat && (
          <div style={{ display: "flex", alignItems: "stretch", flex: "0 0 auto" }}>
            <ChatHistoryPanel
              open={showChatHistory}
              placement="sidebar"
              title="履歴（最新10件）"
              loading={historyLoading}
              threads={threads}
              activeThreadId={currentThreadId}
              onClose={() => setShowChatHistory(false)}
              onRefresh={refreshThreadHistory}
              onLoadThread={async (t) => {
                const tid = t.id || t.threadId;
                if (tid) {
                  setCurrentThreadId(tid);
                }
                await aiChatRef.current?.loadThread(t, "agent");
              }}
            />
            <AIChat
              ref={aiChatRef}
              isOpen={showAIChat}
              onClose={() => setShowAIChat(false)}
              onPreviewDiff={(before, after, nextStructuredDiff) => {
                setDocumentBefore(before);
                setDocumentAfter(after);
                setStructuredDiff(nextStructuredDiff ?? null);
                setIsDiffValid(true);
                setDiffModalOpen(before != null && after != null);
              }}
              height="calc(100vh - 196px)"
              maxHeight="none"
              currentDocumentId={currentProject?.outputs?.["app-design"]?.id || ""}
              currentProjectId={currentProject?.id || ""}
              currentDocumentType="app-design"
              originalDocument={isStructuredDocument ? rawContent : markdownContent}
              threadId={currentSession?.threadId}
              resourceId={currentSession?.resourceId}
              onOpenHistory={async () => {
                setShowChatHistory(true);
                await refreshThreadHistory();
              }}
              onThreadUpdate={refreshThreadHistory}
              onThreadChange={(tid) => setCurrentThreadId(tid)}
            />
          </div>
        )}
      </div>

      {!showAIChat && !isEditable && !isKnowledgeSidebarOpen && shouldShowChatIcon && (
        <div className={chatStyles["floatingButtons"]}>
          <KnowledgeIndicator
            documentType="app-design"
            activeCount={knowledgeCount}
            totalCount={knowledgeTotalCount}
            compact
            onOpen={() => setIsKnowledgeSidebarOpen(true)}
          />
          <button
            type="button"
            className={chatStyles["chatButton"]}
            onClick={() => setShowAIChat(true)}
            aria-label="AIチャットを開く"
          >
            <ChatIcon />
          </button>
        </div>
      )}

      <Modal
        label="差分プレビュー"
        isOpen={isDiffModalOpen && !!documentBefore && !!documentAfter}
        onClose={handleDiffClose}
        className="diffPreviewModal"
        usePortal
        subHeader={diffViewerHeader}
        content={
          !!documentBefore && !!documentAfter ? (
            <div className={chatStyles["diffModalContent"]}>
              <div className={chatStyles["diffModalScroll"]}>
                {isStructuredDocument ? (
                  <StructuredDiffErrorBoundary
                    onError={() => {
                      setIsDiffValid(false);
                      setDiffModalOpen(false);
                      toast.error(TOAST_MESSAGES.AI_CHAT.STRUCTURED_DIFF_PARSE_FAILED, {
                        toastId: "structured-diff-parse-failed",
                      });
                    }}
                  >
                    <StructuredDiffViewer
                      originalContent={
                        typeof documentBefore === "string"
                          ? documentBefore
                          : JSON.stringify(documentBefore)
                      }
                      updatedContent={
                        typeof documentAfter === "string"
                          ? documentAfter
                          : JSON.stringify(documentAfter)
                      }
                      documentType="app-design"
                      structuredDiff={structuredDiff}
                      onValidityChange={setIsDiffValid}
                    />
                  </StructuredDiffErrorBoundary>
                ) : (
                  <DiffViewer
                    ref={diffViewerRef}
                    originalContent={
                      typeof documentBefore === "string"
                        ? documentBefore
                        : JSON.stringify(documentBefore, null, 2)
                    }
                    updatedContent={
                      typeof documentAfter === "string"
                        ? documentAfter
                        : JSON.stringify(documentAfter, null, 2)
                    }
                    onApprovedDocumentChange={handleApprovedDocumentChange}
                    onClose={handleDiffClose}
                    renderHeaderExternally
                    onHeaderChange={setDiffViewerHeader}
                  />
                )}
              </div>
            </div>
          ) : null
        }
        footer={
          <div className={chatStyles["diffModalFooter"]}>
            <button
              type="button"
              className={`${chatStyles["diffFooterButton"]} ${chatStyles["diffFooterButtonSecondary"]}`}
              onClick={handleDiffClose}
              disabled={isDiffSaving}
            >
              キャンセル
            </button>
            <button
              type="button"
              className={`${chatStyles["diffFooterButton"]} ${chatStyles["diffFooterButtonPrimary"]}`}
              onClick={handleDiffApply}
              disabled={isDiffSaving || !documentBefore || !documentAfter || !isDiffValid}
            >
              反映する
            </button>
          </div>
        }
      />
    </div>
  );
};

export default AppDesign;
